// ═══════════════════════════════════════════════════════════════════════════
// collab-protocol — shared lock-scope wire model (client/server WS boundary)
// ═══════════════════════════════════════════════════════════════════════════
//
// LEAF module (zero imports): the CANONICAL definition of a collab soft-lock
// `LockScope` — the specific entity (by DXF handle) or layer (by name) a soft
// lock protects — plus the two pure, total functions that key/compare it.
//
// This is the SHARED source both sides of the `/collab/ws` channel import:
//   • `apps/node-companion/src/collabTypes.ts` re-exports these for the
//     broker (`collabServer.ts`), the lock manager (`locks.ts`), the conflict
//     ledger (`conflict.ts`) and the per-document room (`collabRoom.ts`);
//   • `apps/web/src/app/collab/collabLockTypes.ts` re-exports these for the
//     browser-side LOCK/UNLOCK command surface (`collabLockClient.ts`,
//     `collabLockStore.ts`, `collabLockVeto.ts`, `collabLockFrameCodec.ts`).
//
// `apps/web` must never import from `apps/node-companion` (the companion
// depends on Node's `ws` + other server-only packages — dragging it into the
// browser bundle would either break the build or ship server code to the
// client) and `apps/node-companion` must never import from `apps/web`. This
// package is the THIRD leaf both sides depend on instead — the same
// established precedent `packages/ai-commands` already uses for the sibling
// agent-bridge WS channel (`AGENT_BRIDGE_PROTOCOL_VERSION`, imported
// identically by `wsFrames.ts` and `wsFrameCodec.ts`).
//
// WIRE-PROTOCOL INVARIANT: `lockScopeKey` MUST stay byte-identical between
// the client and the server — the server's lock map AND the client's mirrored
// `locks` map are both keyed by this string, and a divergence here is a
// SILENT correlation failure (a lock the server holds would look unheld to
// the client, or vice versa), not a compile error. Because both sides now
// import the SAME function from this one file, that divergence is no longer
// possible by construction.
// ═══════════════════════════════════════════════════════════════════════════

/** The KIND of thing a soft lock protects (ТЗ Этап B: primitive/layer locks). */
export type LockScopeKind = "entity" | "layer";

/**
 * A lock SCOPE — the specific entity (by DXF handle) or layer (by name) a soft lock
 * protects. Two scopes are the same iff their `kind` + `targetId` match (`lockScopeKey`).
 */
export type LockScope = {
  kind: LockScopeKind;
  targetId: string;
};

/** The canonical string key for a `LockScope` (map key / equality). Total, injective over `{kind,targetId}`. */
export function lockScopeKey(scope: LockScope): string {
  return `${scope.kind}:${scope.targetId}`;
}

/** Whether two lock scopes refer to the same target. */
export function lockScopeEquals(a: LockScope, b: LockScope): boolean {
  return a.kind === b.kind && a.targetId === b.targetId;
}
